import React from 'react';
import { ArrowRight, Search, Square } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { SegButton, Segmented } from './Segmented';
import {
  ACTIVITY_METRICS, BUCKETS, bucketCount, bucketSub, coverage, showingLabel, SORTS, TIERS, tierCount,
} from './queueView';

const BUCKET_COLORS = { overdue: 'text-negative-light', never: 'text-warning', upcoming: 'text-brand-blue' };

// QueueBuckets: Due now, Overdue, Never contacted, Upcoming 7d. Clicking a bucket lists it;
// clicking the chosen one goes back to Due now.
export function QueueBuckets({ summary, filters, onState }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
      {BUCKETS.map((b) => {
        const on = filters.state === b.key;
        const count = bucketCount(b.key, summary);
        const color = b.key === 'overdue' && !count ? 'text-ink' : BUCKET_COLORS[b.key] || 'text-ink';
        return (
          <button
            key={b.key} type="button" aria-pressed={on} onClick={() => onState(on ? 'due' : b.key)}
            className={cn('rounded-xl border bg-surface-1 px-[18px] py-4 text-left transition-colors',
              on ? 'border-cta ring-1 ring-inset ring-cta' : 'border-white/10 hover:border-white/20')}
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-dim">
              <span className={cn('h-2 w-2 rounded-full', b.dot)} />{b.label}
            </div>
            <div className="mt-2 flex items-baseline gap-2.5">
              <span className={cn('text-[30px] font-bold tabular-nums', color)}>{count}</span>
              <span className="text-[13px] text-ink-dim">{bucketSub(b.key, summary, filters.tier)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// QueueToolbar: activity metric, activity tier (with counts), order and search.
export function QueueToolbar({ filters, summary, total, search, onFilters, onSearch }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented label="Activity is measured by" caption="Activity by">
          {ACTIVITY_METRICS.map((m) => (
            <SegButton key={m.key} on={filters.metric === m.key} onClick={() => onFilters({ metric: m.key })}>{m.label}</SegButton>
          ))}
        </Segmented>
        <Segmented label="Filter by activity">
          {TIERS.map((t) => {
            const on = filters.tier === t.key;
            return (
              <SegButton key={t.key} on={on} onClick={() => onFilters({ tier: t.key })}>
                <span className={cn('h-[7px] w-[7px] rounded-full', t.dot)} />
                {t.label}
                <span className={cn('font-medium', on ? 'text-surface-3' : 'text-ink-dim')}>{tierCount(t.key, summary)}</span>
              </SegButton>
            );
          })}
        </Segmented>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Segmented label="Order by" caption="Order by">
          {SORTS.map((s) => (
            <SegButton key={s.key} on={filters.sort === s.key} onClick={() => onFilters({ sort: s.key })}>{s.label}</SegButton>
          ))}
        </Segmented>
        <div className="text-[13px] text-ink-dim">
          Showing <span className="font-bold text-ink">{showingLabel(filters, total)}</span>
        </div>
        <div className="relative ml-auto w-full min-w-[280px] sm:w-auto">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
          <Input
            type="search" value={search} onChange={(e) => onSearch(e.target.value)} maxLength={200}
            placeholder="Email, username or user ID" aria-label="Search players" className="h-11 pl-10"
          />
        </div>
      </div>
    </div>
  );
}

// QueueCoverage: contacted in the last 7 days, and the button that starts (or stops) the
// "Work the queue" mode.
export function QueueCoverage({ summary, working, onWork, disabled }) {
  const { covered, total, pct } = coverage(summary);
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex min-w-[220px] flex-col gap-1.5">
        <div className="flex justify-between text-xs text-ink-dim">
          <span>Contacted in last 7 days</span>
          <span className="font-bold text-ink">{covered} / {total}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
          <div className="h-1.5 rounded-full bg-cta" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {working ? (
        <Button type="button" variant="outline" className="h-11 gap-2 rounded-xl border-cta px-[18px] text-cta hover:text-cta" onClick={onWork}>
          <Square className="h-3.5 w-3.5 fill-current" />Stop working
        </Button>
      ) : (
        <Button type="button" className="h-11 gap-2 rounded-xl px-[18px]" onClick={onWork} disabled={disabled}>
          <ArrowRight className="h-4 w-4" />Work the queue
        </Button>
      )}
    </div>
  );
}
